import React, { useState } from 'react';
import { 
  FiDownload, 
  FiFileText, 
  FiFile, 
  FiPrinter,
  FiChevronDown,
  FiFilter,
  FiDatabase
} from 'react-icons/fi';
import { Snag } from '../../../shared/types/snag';
import SnagTrackerService from '../../../services/snagTrackerService';
import { useToast } from '../../../components/toast/toastProvider';
import './ExportMenu.css';

interface ExportMenuProps {
  allSnags: Snag[];
  filteredSnags: Snag[];
  hasActiveFilters: boolean;
}

const ExportMenu: React.FC<ExportMenuProps> = ({ allSnags, filteredSnags, hasActiveFilters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { addToast } = useToast();

  const handleExport = async (format: 'csv' | 'excel' | 'pdf', useFiltered: boolean = true) => {
    setIsExporting(true);
    
    try {
      const snags = useFiltered ? filteredSnags : allSnags;
      const suffix = useFiltered && hasActiveFilters ? '-filtered' : '';
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `arqite-snags-${timestamp}${suffix}`;

      // Use snagTrackerService to export
      const blob = await SnagTrackerService.exportSnags(format === 'excel' ? 'excel' : format, {
        filtered: useFiltered && hasActiveFilters
      });

      // Trigger file download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addToast({
        type: 'success',
        title: 'Export Successful',
        message: `${snags.length} snags exported as ${format.toUpperCase()}`,
        duration: 4000
      });

    } catch (error) {
      console.error('Export failed:', error);
      addToast({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export data. Please try again.',
        duration: 5000
      });
    } finally {
      setIsExporting(false);
      setIsOpen(false);
    }
  };

  const handleSummaryExport = async () => {
    setIsExporting(true);
    
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `arqite-summary-${timestamp}`;
      
      const blob = await SnagTrackerService.exportSnags('pdf', {
        filtered: hasActiveFilters
      });
      
      // Trigger file download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      addToast({
        type: 'success',
        title: 'Summary Exported',
        message: 'Summary report downloaded successfully',
        duration: 4000
      });
    } catch (error) {
      console.error('Summary export failed:', error);
      addToast({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export summary. Please try again.',
        duration: 5000
      });
    } finally {
      setIsExporting(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="export-menu">
      <button 
        className={`export-btn ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
      >
        <FiDownload />
        Export
        <FiChevronDown className={`chevron ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div className="export-dropdown">
          <div className="export-section">
            <div className="export-section-header">
              <FiFilter />
              <span>Filtered Data ({filteredSnags.length} snags)</span>
            </div>
            
            <button 
              className="export-option"
              onClick={() => handleExport('csv', true)}
              disabled={isExporting}
            >
              <FiFileText />
              <div className="export-option-content">
                <span>Export to CSV</span>
                <small>Spreadsheet format with all details</small>
              </div>
            </button>

            <button 
              className="export-option"
              onClick={() => handleExport('excel', true)}
              disabled={isExporting}
            >
              <FiFile />
              <div className="export-option-content">
                <span>Export to Excel</span>
                <small>Excel-compatible format</small>
              </div>
            </button>

            <button 
              className="export-option"
              onClick={() => handleExport('pdf', true)}
              disabled={isExporting}
            >
              <FiPrinter />
              <div className="export-option-content">
                <span>Export to PDF</span>
                <small>Formatted report for printing</small>
              </div>
            </button>
          </div>

          {hasActiveFilters && (
            <div className="export-section">
              <div className="export-section-header">
                <FiDatabase />
                <span>All Data ({allSnags.length} snags)</span>
              </div>
              
              <button 
                className="export-option"
                onClick={() => handleExport('csv', false)}
                disabled={isExporting}
              >
                <FiFileText />
                <div className="export-option-content">
                  <span>Export All to CSV</span>
                  <small>Complete dataset, ignores filters</small>
                </div>
              </button>
            </div>
          )}

          <div className="export-section">
            <button 
              className="export-option summary"
              onClick={handleSummaryExport}
              disabled={isExporting}
            >
              <FiFile />
              <div className="export-option-content">
                <span>Summary Report</span>
                <small>Overview statistics and metrics</small>
              </div>
            </button>
          </div>
        </div>
      )}

      {isExporting && (
        <div className="export-loading">
          <div className="loading-spinner"></div>
          <span>Exporting...</span>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
